import { defineComponent as _defineComponent } from 'vue'
import { useMyProjects } from '@/views/my-projects/composables/useMyProjects';
import {
  PropType, computed, ref, toRefs
} from 'vue';
import { ProjectOffer, ProjectOfferRequest } from '@/models/MyProjects';
import useFormattedDateTime from '@/mixins/useFormattedDateTime';
import BCXMarkdown from '@/components/molecules/BCXMarkdown.vue';
import useUser from '@/mixins/useUser';
import xImage from '@/components/svg/x_large.svg';
import okImage from '@/components/svg/ok_large.svg';
import { updateOffer } from '@/services/OffersService';
import BCXButton from '@/components/molecules/BCXButton.vue';
import BCXDatepickerField from '@/components/molecules/forms/datepicker/BCXDatepickerField.vue';
import { whenever } from '@vueuse/core';


export default /*#__PURE__*/_defineComponent({
  __name: 'MyProjectsOfferDetailConditions',
  props: {
  offer: {
    type: Object as PropType<ProjectOffer>,
    required: true
  },
  projectId: {
    type: String,
    default: undefined
  },
  bgImage: {
    type: String as PropType<'none' | 'accepted' | 'declined'>,
    default: 'none'
  },
  extendValidity: {
    type: Boolean,
    default: false
  }
},
  emits: ['extend-validity-abort', 'updated-offer'],
  setup(__props, { emit }) {

const props = __props;





const { extendValidity: extendValidityPropRef, offer: offerPropRef } = toRefs(props);

const { tUserFallback: t } = useUser('self');

const { getFormattedDate } = useFormattedDateTime();

const { isNarrow } = useMyProjects();

const backgroundStyle = computed(() => {
  let imageUrl = '';
  if (props.bgImage === 'accepted') {
    imageUrl = okImage;
  } else if (props.bgImage === 'declined') {
    imageUrl = xImage;
  }

  return {
    backgroundImage: imageUrl ? `url(${imageUrl})` : 'none',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  };
});

const calculateDateDifference = (date1: Date, date2: Date) => {
  if (!date1 || !date2) return 0;
  const diff = date1.getTime() - date2.getTime();
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
  if (Number.isNaN(days) || days < 0) return 0;
  return days;
};

const offerValidUntilDate = ref(props.offer.validUntil);
const newOfferValidUntilDate = ref(props.offer.validUntil);
const offerValidDurationDays = computed(() => calculateDateDifference(new Date(offerPropRef.value.validUntil), new Date()));
const newOfferValidDurationDays = computed(() => calculateDateDifference(new Date(newOfferValidUntilDate.value), new Date()));

const isExpired = computed(() => offerValidDurationDays.value < 1 && offerPropRef.value.status === 'EXPIRED');
const offerExpiredText = t('my-projects.offers-detail.conditions.expired');

const isDaysLeftDisplayed = computed(() => {
  const statuses = ['OPEN', 'OPEN_CHANGED', 'NEGOTIATIONS', 'QUEUED'];
  return statuses.includes(offerPropRef.value.status);
});

const warnAboutShortDuration = computed(() => offerValidDurationDays.value < 8);
const offerValidDurationDaysText = computed(() => (offerValidDurationDays.value === 1
  ? t('my-projects.offers-detail.conditions.valid-until-today')
  : t('my-projects.offers-detail.conditions.valid-until-days', { days: offerValidDurationDays.value })));

const offerValidDurationDaysEditText = computed(() => (newOfferValidDurationDays.value === 1
  ? t('project-forum.apply-box.offer-valid-until-today')
  : t('project-forum.apply-box.offer-valid-until-days', { days: newOfferValidDurationDays.value })));

const saveNewValidUntil = async () => {
  const offerRequest: ProjectOfferRequest = {
    billingAmount: props.offer.billingAmount,
    billingUnit: props.offer.billingUnit,
    startDate: props.offer.startDate,
    validUntil: `${newOfferValidUntilDate.value}T${new Date().toLocaleTimeString()}`,
    hoursPerWeek: props.offer.hoursPerWeek,
    dependingOnComplexity: props.offer.dependingOnComplexity,
    remoteWork: props.offer.remoteWork,
    message: props.offer.message,
    hoursPerWeekChanged: false,
    remoteWorkChanged: false,
    startDateChanged: false,
  };
  try {
    const offer = await updateOffer(props.projectId, props.offer.id, offerRequest);
    emit('updated-offer', offer);
  } catch (error) {
    console.error('Error while updating offer', error);
  }
};

const abortValidityExtension = () => {
  emit('extend-validity-abort');
};

whenever(extendValidityPropRef, () => {
  newOfferValidUntilDate.value = props.offer.validUntil;
});

const earliestValidUntilDate = computed(() => {
  const date = new Date(offerPropRef.value.created);
  date.setDate(date.getDate() + 4);
  if (date.getTime() < new Date().getTime()) {
    return new Date().setDate(new Date().getDate() + 4);
  }
  return date;
});

const adjustedStatus = computed(() => {
  let { status } = offerPropRef.value;
  if (status === 'AUTO_REJECTED') status = 'DECLINED';
  else if (status === 'WITHDRAWN_QUEUED') status = 'WITHDRAWN';
  else if (status === 'OPEN_CHANGED') status = 'OPEN';
  return status;
});

return { __sfc: true,props, emit, extendValidityPropRef, offerPropRef, t, getFormattedDate, isNarrow, backgroundStyle, calculateDateDifference, offerValidUntilDate, newOfferValidUntilDate, offerValidDurationDays, newOfferValidDurationDays, isExpired, offerExpiredText, isDaysLeftDisplayed, warnAboutShortDuration, offerValidDurationDaysText, offerValidDurationDaysEditText, saveNewValidUntil, abortValidityExtension, earliestValidUntilDate, adjustedStatus, BCXMarkdown, BCXButton, BCXDatepickerField }
}

})